<template>
  <div class="h-full b-rounded-1 contact-selector__selected px-4 py-2 flex flex-col">
    <div class="flex items-center mb-1 contact-selector__selected-header">
      <CH6 :class="simpleView ? 'ma-0' : 'my-2'">
        {{ t('canvasUI.CContactSelectorSelected.selected') }} ({{
          (selectedAccountIds?.length ?? 0) + (selectedContactIds?.length ?? 0) + (selectedColleagueIds?.length ?? 0)
        }})
      </CH6>
      <div v-if="selectedItems && simpleView && allowAccountChange" class="ml-a">
        <CButton
          :disabled="loading"
          quaternary
          :style="{
            '--n-color-pressed': themeVars.error2,
            '--n-color-focus': themeVars.error2,
            '--n-color-hover': themeVars.error2,
          }"
          type="error"
          @click="$emit('remove-all')"
        >
          {{ t('canvasUI.CContactSelectorSelected.removeAll') }}
        </CButton>
      </div>
    </div>
    <div class="flex contact-selector__list-items" :class="{ 'calendar-drag-list': isDraggable }">
      <div v-if="selectedItems && isDraggable" class="flex items-center cursor-grab contact-selector__drag-grip">
        <CIcon class="pl-1 pr-3" :color="themeVars.primary4" fa-type="fas" icon="grip-dots-vertical" :size="16" />
      </div>
      <div
        v-if="selectedItems"
        class="contact-selector__selected-list flex-1"
        :class="{ 'contact-selector__selected-list--short': simpleView }"
      >
        <div
          v-for="account in selectedAccountsWithContacts"
          :key="account.Id"
          class="contact-selector__selected__account b-rounded-1"
        >
          <div class="contact-selector__selected__account__info flex items-center ml-4">
            <div class="contact-selector__selected__account__info__left">
              <div class="font-600 underline truncate">
                {{ account.Name }}
              </div>
              <div
                v-if="account.BillingAddress?.street"
                class="contact-selector__selected__account__info__left__address truncate"
              >
                {{ account.BillingAddress?.street }}
              </div>
            </div>
            <div class="contact-selector__selected__account__info__right flex items-center justify-end">
              <CButton
                circle
                class="mr-2 selected-contact-button-color"
                :disabled="loading || !allowAccountChange"
                @click="$emit('remove-account', account.Id)"
              >
                <CIcon fa-type="fas" icon="close" />
              </CButton>
            </div>
          </div>
          <div
            v-for="contact in account.contacts"
            :key="(contact as any).Id"
            class="contact-selector__selected__account__contact flex items-center"
          >
            <div class="contact-selector__selected__account__contact__content font-600 underline truncate">
              <CIcon class="mx-4" :color="themeVars.primary4" fa-type="fas" icon="address-card" :size="16" />
              {{ (contact as any).Name }}
            </div>
            <div class="contact-selector__selected__account__contact__right flex justify-end items-center">
              <CButton
                circle
                class="mr-2 selected-contact-button-color"
                :disabled="loading"
                @click="$emit('remove-contact', (contact as any).Id)"
              >
                <CIcon fa-type="fas" icon="close" />
              </CButton>
            </div>
          </div>
        </div>
        <div v-if="accounts.length && contacts.length">
          <div
            v-for="contact in orphanContacts"
            :key="contact.Id"
            class="mt-2 contact-selector__selected__orphan-contact b-rounded-1 flex items-center"
          >
            <div class="contact-selector__selected__orphan-contact__left">
              <CIcon class="mx-4" :color="themeVars.primary4" fa-type="fas" icon="address-card" :size="16" />
            </div>
            <div class="contact-selector__selected__orphan-contact__content">
              <div class="contact-selector__selected__orphan-contact__content__name font-600 underline truncate">
                {{ contact.Name }}
              </div>
              <div class="contact-selector__selected__orphan-contact__content__account-name truncate">
                {{ accountsById[contact.AccountId]?.Name }}
              </div>
            </div>
            <div class="contact-selector__selected__orphan-contact__right">
              <CButton
                circle
                class="mr-2 selected-contact-button-color"
                :disabled="loading"
                @click="$emit('remove-contact', contact.Id)"
              >
                <CIcon fa-type="fas" icon="close" />
              </CButton>
            </div>
          </div>
        </div>
        <div
          v-for="colleague in selectedColleagues"
          :key="colleague.Id"
          class="mt-2 contact-selector__selected__colleague b-rounded-1 flex items-center"
        >
          <div class="contact-selector__selected__colleague__left">
            <CIcon class="mx-4" :color="themeVars.primary4" icon="user" :size="16" />
          </div>
          <div class="contact-selector__selected__colleague__content">
            <div class="contact-selector__selected__colleague__content__name font-600 underline truncate">
              {{ colleague.FirstName + ' ' + colleague.LastName }}
            </div>
            <div class="contact-selector__selected__colleague__content__email truncate">
              {{ colleague.Email }}
            </div>
          </div>
          <div class="contact-selector__selected__colleague__right">
            <CButton circle class="mr-2" :disabled="loading" @click="$emit('remove-colleague', colleague.Id)">
              <CIcon fa-type="fas" icon="close" />
            </CButton>
          </div>
        </div>
      </div>
      <div v-else class="contact-selector__selected-empty">
        <CIcon class="mb-2" fa-type="fas" icon="empty-set" size="24px" />
        <span class="text-size-xs">{{ t('canvasUI.CContactSelectorSelected.nothingSelected') }}</span>
      </div>
    </div>
    <div v-if="selectedItems && !simpleView && allowAccountChange" class="mt-4 flex items-center justify-center">
      <CButton :disabled="loading" secondary strong type="error" @click="$emit('remove-all')">
        {{ t('canvasUI.CContactSelectorSelected.removeAll') }}
      </CButton>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { CH6 } from '@lib/main.lib'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

const { t } = useI18n()

defineEmits<{
  (e: 'remove-contact', contactId: string): void
  (e: 'remove-account', accountId: string): void
  (e: 'remove-colleague', userId: string): void
  (e: 'remove-all'): void
}>()
const props = withDefaults(
  defineProps<{
    accounts?: any[]
    colleagues?: any[]
    contacts?: any[]
    loading?: boolean
    selectedAccountIds?: string[]
    selectedColleagueIds?: string[]
    selectedContactIds?: string[]
    simpleView?: boolean
    allowAccountChange?: boolean
    isDraggable?: boolean
  }>(),
  {
    colleagues: () => [],
    contacts: () => [],
    accounts: () => [],
    selectedColleagueIds: () => [],
    selectedContactIds: () => [],
    selectedAccountIds: () => [],
    simpleView: false,
    loading: false,
    allowAccountChange: true,
    isDraggable: false,
  },
)
const themeVars = useThemeVars()

const accountsById = computed(
  () =>
    props.accounts?.reduce((acc: Record<string, any>, account: any) => {
      acc[account.Id] = account
      return acc
    }, {}) ?? {},
)
const contactsById = computed(
  () =>
    props.contacts?.reduce((acc: Record<string, any>, contact: any) => {
      acc[contact.Id] = contact
      return acc
    }, {}) ?? {},
)
const selectedAccountsWithContacts = computed(() =>
  (props.selectedAccountIds ?? []).map((accountId: string) => ({
    ...accountsById.value[accountId],
    contacts: props.selectedContactIds.reduce(
      (acc: any[], contactId: string) =>
        contactsById.value[contactId]?.AccountId === accountId ? [...acc, contactsById.value[contactId]] : acc,
      [],
    ),
  })),
)
const orphanContacts = computed(() =>
  (props.selectedContactIds ?? [])
    .reduce(
      (acc: any[], selectedContactId: string) =>
        !props.selectedAccountIds?.includes(contactsById.value[selectedContactId]?.AccountId)
          ? [...acc, contactsById.value[selectedContactId]]
          : acc,
      [],
    )
    .filter(Boolean),
)

const selectedColleagues = computed(() =>
  props.colleagues?.filter((colleague: any) => props.selectedColleagueIds?.includes(colleague.Id)),
)

const selectedItems = computed(
  () => selectedAccountsWithContacts.value.length || orphanContacts.value.length || selectedColleagues.value.length,
)
</script>

<style scoped lang="scss">
.contact-selector__selected {
  border: 1px solid v-bind('themeVars.primary5');
  min-height: 120px;
}

.contact-selector__selected-header {
  height: 36px;
}

.contact-selector__selected-list {
  overflow-y: auto;

  &--short {
    max-height: 150px;
  }
}

.contact-selector__selected-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: v-bind('themeVars.primary4');
}

.contact-selector__selected__account {
  background: v-bind('themeVars.primary6');
  border-right: 4px solid v-bind('themeVars.primary3');
}

.contact-selector__selected__account__info {
  height: 58px;
}

.contact-selector__selected__account__contact {
  border-top: 1px solid v-bind('themeVars.primary5');
  height: 48px;
}

.contact-selector__selected__account__info__left,
.contact-selector__selected__account__contact__content {
  flex: 1;
  min-width: 0;
}

.contact-selector__selected__account__info__right,
.contact-selector__selected__account__contact__right {
  flex: 0 0 48px;
}

.contact-selector__selected__colleague__content__email,
.contact-selector__selected__orphan-contact__content__account-name,
.contact-selector__selected__account__info__left__address {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text2');
}

.contact-selector__selected__orphan-contact,
.contact-selector__selected__colleague {
  background: v-bind('themeVars.primary6');
  height: 50px;
}

.contact-selector__selected__colleague__left,
.contact-selector__selected__orphan-contact__left {
  flex: 0 0 36px;
}

.contact-selector__selected__colleague__content,
.contact-selector__selected__orphan-contact__content {
  flex: 1;
  min-width: 0;
}

.contact-selector__selected__colleague__right,
.contact-selector__selected__orphan-contact__right {
  flex: 0 0 48px;
}
.contact-selector__list-items {
  max-height: 56svh;
  overflow: auto;
}
.selected-contact-button-color {
  background-color: v-bind('themeVars.primary5');
}
</style>
